import { useEffect } from 'react';
import { Project } from '../../../mocks/projekte';

interface ProjectModalProps {
  project: Project | null;
  onClose: () => void;
}

const categoryColors: Record<string, string> = {
  'Sanierung & Renovierung': 'bg-amber-100 text-amber-800',
  'Garten- & Landschaftsbau': 'bg-green-100 text-green-800',
  'Trockenbau': 'bg-blue-100 text-blue-800',
  'Fassade & Außenwände': 'bg-stone-100 text-stone-800',
  'Bodenbeläge': 'bg-orange-100 text-orange-800',
  'Malerarbeiten': 'bg-pink-100 text-pink-800',
  'Bauzeichnungen': 'bg-teal-100 text-teal-800',
};

export default function ProjectModal({ project, onClose }: ProjectModalProps) {
  useEffect(() => {
    if (project) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => { document.body.style.overflow = ''; };
  }, [project]);

  useEffect(() => {
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
    };
    window.addEventListener('keydown', handleKey);
    return () => window.removeEventListener('keydown', handleKey);
  }, [onClose]);

  if (!project) return null;

  const badgeClass = categoryColors[project.category] ?? 'bg-gray-100 text-gray-800';

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6"
      role="dialog"
      aria-modal="true"
    >
      {/* Overlay */}
      <div
        className="absolute inset-0 bg-black/70 backdrop-blur-sm"
        onClick={onClose}
      />

      {/* Modal Box */}
      <div className="relative bg-white rounded-2xl overflow-hidden w-full max-w-3xl max-h-[90vh] flex flex-col shadow-2xl">
        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-4 right-4 z-10 w-10 h-10 flex items-center justify-center bg-white/90 hover:bg-white rounded-full cursor-pointer transition-colors"
          aria-label="Schließen"
        >
          <i className="ri-close-line text-xl text-gray-800" />
        </button>

        {/* Image */}
        <div className="relative w-full h-64 sm:h-80 flex-shrink-0">
          <img
            src={project.image}
            alt={project.title}
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
          <div className="absolute bottom-4 left-5">
            <span className={`text-xs font-semibold px-3 py-1 rounded-full ${badgeClass}`}>
              {project.category}
            </span>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto p-6 sm:p-8 flex-1">
          <h2 className="text-2xl sm:text-3xl font-bold text-gray-900 mb-2">{project.title}</h2>

          {/* Meta */}
          <div className="flex flex-wrap gap-4 mb-5 text-sm text-gray-500">
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-map-pin-line text-[#0D2B4E]" />
              </div>
              {project.location}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-calendar-line text-[#0D2B4E]" />
              </div>
              {project.year}
            </span>
            <span className="flex items-center gap-1.5">
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-time-line text-[#0D2B4E]" />
              </div>
              Laufzeit: {project.duration}
            </span>
          </div>

          <p className="text-gray-600 leading-relaxed mb-6">{project.description}</p>

          {/* Details list */}
          <div>
            <h3 className="text-sm font-semibold text-gray-900 uppercase tracking-wider mb-3">
              Leistungsumfang
            </h3>
            <ul className="space-y-2">
              {project.details.map((detail) => (
                <li key={detail} className="flex items-start gap-2.5 text-sm text-gray-700">
                  <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                    <i className="ri-checkbox-circle-fill text-[#0D2B4E]" />
                  </div>
                  {detail}
                </li>
              ))}
            </ul>
          </div>

          {/* CTA */}
          <div className="mt-8 pt-6 border-t border-gray-100">
            <a
              href="/kontakt"
              className="inline-flex items-center gap-2 bg-[#0D2B4E] text-white px-6 py-3 rounded-lg hover:bg-[#0a2240] transition-colors font-semibold text-sm cursor-pointer whitespace-nowrap"
            >
              <div className="w-4 h-4 flex items-center justify-center">
                <i className="ri-phone-line" />
              </div>
              Ähnliches Projekt anfragen
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
